import { Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const EFFECTIVE = '14 May 2025';
const PRIVACY_EMAIL = 'privacy@transitdeal.co.uk';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="ml-0">
      <h3 className="text-sm font-semibold text-gray-800 mb-1.5">{title}</h3>
      <div className="space-y-2 text-sm text-gray-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="list-disc list-inside space-y-1 text-gray-600 ml-2">
      {items.map((i, idx) => <li key={idx}>{i}</li>)}
    </ul>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <div className="page-shell-prose">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-1">
          <Shield className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mt-1">
            Effective date: {EFFECTIVE} &nbsp;·&nbsp; TransitDeal Ltd
          </p>
        </div>
      </div>

      <div className="card p-6 sm:p-8">

        <Section title="1. Who We Are">
          <p>
            TransitDeal Ltd (<strong>"TransitDeal"</strong>, <strong>"we"</strong>, <strong>"us"</strong> or <strong>"our"</strong>)
            is a company registered in England and Wales (Company No. [COMPANY_REG_NO]), with our registered
            office at [REGISTERED_ADDRESS]. We operate the TransitDeal platform — a UK logistics marketplace
            connecting customers with drivers, hauliers, and workshops.
          </p>
          <p>
            We are the <strong>data controller</strong> for personal information we collect about you. We are registered
            with the Information Commissioner's Office (ICO), registration number [ICO_REG_NO].
          </p>
          <p>
            If you have any questions about how we handle your personal data, please contact us at{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-600 hover:underline">{PRIVACY_EMAIL}</a>.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <Sub title="2.1 Account and Identity Information">
            <p>When you register or update your profile, we collect:</p>
            <Ul items={[
              'Full name, email address, and phone number',
              'Home or business address',
              'Account role (Customer, Driver, Workshop, Haulier)',
              'Business details (company name, VAT number, Companies House number) where applicable',
            ]} />
          </Sub>

          <Sub title="2.2 Professional and Compliance Information (Drivers and Workshops)">
            <p>To verify that drivers and workshops meet legal requirements, we collect:</p>
            <Ul items={[
              'DVLA driving licence number and category',
              'Vehicle registration number and type',
              'Hire & Reward (H&R) insurance details (insurer, policy reference, expiry)',
              'Goods in Transit (GIT) insurance details',
              'Public Liability Insurance details',
              'MOT certificate information',
              'Scanned copies or photographs of the above documents',
              'Preferred working areas (postcodes)',
            ]} />
          </Sub>

          <Sub title="2.3 Job and Transaction Data">
            <Ul items={[
              'Details of jobs posted, quoted on, accepted, and completed',
              'Pickup and delivery addresses (including latitude/longitude)',
              'Goods descriptions and estimated values',
              'Quote amounts and accepted contract prices',
              'Earnings history and payout records',
            ]} />
          </Sub>

          <Sub title="2.4 Payment Information">
            <p>
              Payments are processed by <strong>Stripe Payments Europe, Ltd</strong> ("Stripe"), our payment
              service provider. We do not store full card numbers, CVV codes, or bank account details on our
              servers. We receive confirmation of payment outcomes and hold transaction reference numbers.
              Stripe's own privacy policy applies to payment data — see{' '}
              <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">stripe.com/gb/privacy</a>.
            </p>
          </Sub>

          <Sub title="2.5 Communications and Chatbot Data">
            <Ul items={[
              'Messages exchanged between users about specific jobs',
              'Dispute correspondence',
              'Text and audio inputs to the AI assistant (chatbot)',
              'Transcriptions of voice interactions',
            ]} />
          </Sub>

          <Sub title="2.6 Technical and Usage Data">
            <Ul items={[
              'IP address and approximate location (country/city)',
              'Browser type and version, device type and operating system',
              'Pages visited, features used, timestamps, and session duration',
              'Push notification tokens (mobile app)',
              'Error logs and crash reports',
            ]} />
          </Sub>

          <Sub title="2.7 Information From Third Parties">
            <p>
              We receive identity and authentication data from <strong>our own authentication service</strong> (hosted on our own domain) when you sign in,
              including your email address and name as held with your identity provider.
            </p>
          </Sub>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use personal information to:</p>
          <Ul items={[
            'Create and manage your account and verify your identity',
            'Match jobs with suitable drivers based on location, vehicle type, and preferences',
            'Process payments and maintain financial records',
            'Verify driver compliance documents (licence, insurance, MOT)',
            'Send job alerts, push notifications, and service communications',
            'Enable messaging between job parties',
            'Resolve disputes and manage claims',
            'Operate and improve the AI assistant and voice features',
            'Detect, prevent, and investigate fraud and platform abuse',
            'Comply with legal and regulatory obligations',
            'Improve platform features through aggregated, anonymised analytics',
          ]} />
        </Section>

        <Section title="4. Legal Basis for Processing (UK GDPR)">
          <p>We rely on the following legal bases under Article 6 UK GDPR:</p>

          <Sub title="4.1 Performance of a Contract (Art. 6(1)(b))">
            <p>Processing your account data, job data, compliance documents, and payment information is necessary to deliver the TransitDeal service you have signed up for.</p>
          </Sub>

          <Sub title="4.2 Legitimate Interests (Art. 6(1)(f))">
            <p>We process technical and usage data, fraud-prevention signals, and anonymised analytics where our business interest in improving and securing the platform does not override your rights and freedoms.</p>
          </Sub>

          <Sub title="4.3 Legal Obligation (Art. 6(1)(c))">
            <p>We retain financial records for 7 years to comply with HMRC requirements, and may disclose information to law enforcement or regulators when legally compelled to do so.</p>
          </Sub>

          <Sub title="4.4 Consent (Art. 6(1)(a))">
            <p>Where we rely on consent (e.g. for analytics cookies or optional voice features), you can withdraw consent at any time by updating your preferences or contacting us. Withdrawal does not affect the lawfulness of processing before withdrawal.</p>
          </Sub>
        </Section>

        <Section title="5. Sharing Your Information">
          <Sub title="5.1 Other Platform Users">
            <p>When a job is matched, relevant profile information (name, vehicle type, rating) is shared between the job's parties. Full addresses, licence numbers, and financial details are never publicly displayed.</p>
          </Sub>

          <Sub title="5.2 Service Providers (Sub-processors)">
            <p>We share data with carefully selected third parties who act as data processors on our behalf:</p>
            <Ul items={[
              "IONOS — cloud hosting, storage and email delivery. Data is processed within the EEA.",
              "OpenAI — AI features. Data may be processed in the US under Standard Contractual Clauses.",
              "Microsoft Azure — mapping and routing services only (Azure Maps).",
              "Stripe — payment processing. Governed by Stripe's DPA.",
              'Expo / EAS — mobile app build and push notification infrastructure.',
            ]} />
          </Sub>

          <Sub title="5.3 Legal and Regulatory Disclosure">
            <p>We may disclose personal data to courts, regulators, the ICO, DVLA, or law enforcement where required by law, a valid legal process, or to protect the safety of our users and the public.</p>
          </Sub>

          <Sub title="5.4 Business Transfers">
            <p>In the event of a merger, acquisition, or sale of assets, personal data held by us may be transferred to a successor entity. We will notify you before your data is transferred and becomes subject to a different privacy policy.</p>
          </Sub>

          <p>We do not sell, rent, or trade your personal data to third parties for their own marketing purposes.</p>
        </Section>

        <Section title="6. International Data Transfers">
          <p>
            Your data is primarily processed within the United Kingdom and the European Economic Area.
            Our cloud provider Microsoft Azure may transfer data to the United States and other countries
            where Microsoft operates data centres. Such transfers are made under the UK International Data
            Transfer Agreement (IDTA) or the EU Standard Contractual Clauses (SCCs) adopted by Microsoft,
            which provide equivalent protection under UK GDPR.
          </p>
        </Section>

        <Section title="7. How Long We Keep Your Data">
          <Ul items={[
            'Account and profile data — for the duration of your account, plus 7 years after closure (to meet HMRC and contractual obligations)',
            'Transaction and payment records — 7 years (HMRC statutory requirement)',
            'Compliance documents (scanned licences, insurance) — while your account is active, plus 1 year after expiry of the document or closure of your account, whichever is later',
            'Job and contract records — 6 years after completion (Limitation Act 1980)',
            'User messages and dispute correspondence — 3 years after the job is closed',
            'Chatbot conversation logs — 12 months',
            'Technical logs and error reports — 90 days',
            'Push notification tokens — until revoked or the device is removed from your account',
          ]} />
          <p>Where we no longer need your data we will securely delete or anonymise it.</p>
        </Section>

        <Section title="8. Your Rights">
          <p>Under UK GDPR you have the following rights. To exercise any of them, contact us at{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-600 hover:underline">{PRIVACY_EMAIL}</a>:
          </p>
          <Ul items={[
            'Right of access — to receive a copy of the personal data we hold about you (Subject Access Request)',
            'Right to rectification — to have inaccurate or incomplete data corrected',
            'Right to erasure ("right to be forgotten") — to request deletion of your data where we no longer have a lawful reason to hold it',
            'Right to restriction — to ask us to pause processing your data in certain circumstances',
            'Right to data portability — to receive your data in a structured, machine-readable format',
            'Right to object — to processing based on legitimate interests, or to direct marketing',
            'Right to withdraw consent — at any time, where we process data on the basis of consent',
            'Rights in relation to automated decision-making — including the right not to be subject to purely automated decisions that significantly affect you',
          ]} />
          <p>We will respond to requests within <strong>one calendar month</strong>. Where requests are complex or numerous, we may extend this by up to two further months and will notify you accordingly.</p>
          <p>
            If you are not satisfied with our response, you have the right to lodge a complaint with the
            Information Commissioner's Office (ICO) at{' '}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ico.org.uk</a>{' '}
            or by calling <strong>0303 123 1113</strong>.
          </p>
        </Section>

        <Section title="9. Cookies and Local Storage">
          <p>
            We use browser local storage and session storage (not traditional HTTP cookies) for authentication
            and preference management. For full details of what we store, why, and how to manage it, see our{' '}
            <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            The TransitDeal platform is intended for users aged <strong>18 and over</strong>. We do not
            knowingly collect personal data from anyone under the age of 18. If you believe we have
            inadvertently collected data from a minor, please contact us immediately at{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-600 hover:underline">{PRIVACY_EMAIL}</a>{' '}
            and we will delete it promptly.
          </p>
        </Section>

        <Section title="11. Security">
          <p>
            We implement appropriate technical and organisational measures to protect your personal data against
            unauthorised access, loss, or destruction. These include encryption in transit (TLS/HTTPS),
            encryption at rest, role-based access controls, and regular security reviews. While we take
            reasonable precautions, no internet transmission is completely secure, and we cannot guarantee
            absolute security.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we make material changes, we will notify
            you by email (to the address registered on your account) and/or by a prominent notice on the platform
            at least 14 days before the change takes effect. Your continued use of the platform after that date
            constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>For any privacy-related enquiries, to exercise your rights, or to report a concern:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mt-2">
            <p className="font-semibold text-gray-900">TransitDeal Ltd — Privacy Team</p>
            <p className="text-gray-600">Email: <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-600 hover:underline">{PRIVACY_EMAIL}</a></p>
            <p className="text-gray-600">Address: [REGISTERED_ADDRESS]</p>
          </div>
        </Section>
      </div>
    </div>
  );
}
